import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from 'contexts/NotificationContext';
import { uploadsApi } from 'lib/api';

const pollBatchStatus = async (batchId: string, onUpdate?: (data: any) => void) => {
  if (!batchId) return null;
  let isCompleted = false;
  let attempts = 0;
  let finalData: any = null;
  while (!isCompleted && attempts < 60) {
    await new Promise(resolve => setTimeout(resolve, 2000));
    try {
      const res = await uploadsApi.getBatchStatus(batchId);
      finalData = res.data;
      if (onUpdate) onUpdate(finalData); // Notify caller of real-time status changes
      const st = res.data.status;
      if (st === 'completed' || st === 'completed_with_errors' || st === 'failed') {
        isCompleted = true;
      }
    } catch {
      // Ignorer les erreurs réseau temporaires
    }
    attempts++;
  }
  return finalData;
};

interface AutoSyncContextType {
  isSyncActive: boolean;
  setIsSyncActive: (active: boolean) => void;
  syncInterval: number;
  setSyncInterval: (interval: number) => void;
  autoSyncTeamId: number | null;
  setAutoSyncTeamId: (teamId: number | null) => void;
  lastSyncTime: string | null;
  pendingFiles: any[];
  isSyncing: boolean;
  isConnected: boolean;
  triggerSync: (daysToFetch?: number) => Promise<void>;
  handleImportPending: (file: any) => Promise<void>;
  handleDeletePending: (file: any, permanentlyIgnore?: boolean) => void;
}

const AutoSyncContext = createContext<AutoSyncContextType | undefined>(undefined);

export const AutoSyncProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();

  const [isSyncActive, setIsSyncActive] = useState(() => localStorage.getItem('selektia_auto_sync_active') === 'true');
  const [syncInterval, setSyncInterval] = useState(() => Number(localStorage.getItem('selektia_auto_sync_interval')) || 5);
  const [autoSyncTeamId, setAutoSyncTeamId] = useState<number | null>(() => {
    const saved = localStorage.getItem('selektia_auto_sync_team_id');
    return saved ? Number(saved) : null;
  });
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(() => localStorage.getItem('selektia_auto_sync_last'));
  const [pendingFiles, setPendingFiles] = useState<any[]>(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('selektia_auto_sync_pending') || '[]'); 
      const todayStart = new Date();
      todayStart.setHours(0,0,0,0);
      return saved.filter((f: any) => !f.is_duplicate && (!f.syncDate || f.syncDate >= todayStart.getTime()));
    } 
    catch { return []; }
  });
  
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConnected, setIsConnected] = useState(!!localStorage.getItem('selektia_email_connection'));

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleStorageChange = () => {
      const currentlyConnected = !!localStorage.getItem('selektia_email_connection');
      setIsConnected(currentlyConnected);
      if (!currentlyConnected) {
          setIsSyncActive(false);
          setPendingFiles([]);
          setLastSyncTime(null);
          localStorage.removeItem('selektia_auto_sync_deleted_files');
        }
    };
    window.addEventListener('storage', handleStorageChange);
    const iv = setInterval(handleStorageChange, 2000);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('selektia_auto_sync_active', String(isSyncActive));
    localStorage.setItem('selektia_auto_sync_interval', String(syncInterval));
    if (autoSyncTeamId !== null) {
      localStorage.setItem('selektia_auto_sync_team_id', String(autoSyncTeamId));
    } else {
      localStorage.removeItem('selektia_auto_sync_team_id');
    }
    localStorage.setItem('selektia_auto_sync_pending', JSON.stringify(pendingFiles));
    if (lastSyncTime) localStorage.setItem('selektia_auto_sync_last', lastSyncTime);
  }, [isSyncActive, syncInterval, autoSyncTeamId, pendingFiles, lastSyncTime]);

  const triggerSync = async (daysToFetch = 1) => {
    if (!isConnected || isSyncing) return;
    try {
      setIsSyncing(true);
      const connStr = localStorage.getItem('selektia_email_connection');
      if (!connStr) return;
      const { provider } = JSON.parse(connStr);
      
      const response = (provider === 'imap' || provider === 'exchange')
        ? await uploadsApi.fetchImapPdfs(daysToFetch)
        : await uploadsApi.fetchGmailPdfs(daysToFetch);
        
      const files = response.data.pdfs || [];
      const now = Date.now();
      
      const deletedFilesCache = JSON.parse(localStorage.getItem('selektia_auto_sync_deleted_files') || '[]');
      const validFiles = files.filter((f: any) => {
        if (f.is_duplicate) return false;
        const uniqueKey = `${f.filename}-${f.sender}-${f.subject}`;
        if (deletedFilesCache.includes(uniqueKey)) return false;
        return true;
      });
      const applications = validFiles.filter((f: any) => f.is_application !== false);
      const normalEmails = validFiles.filter((f: any) => f.is_application === false);
      
      // Log sync trigger
      if (validFiles.length > 0) {
        validFiles.forEach((f: any) => {
          uploadsApi.logAutoSyncAction({
            action: 'sync_triggered',
            filename: f.filename,
            sender: f.sender,
            subject: f.subject,
            status: 'success',
            details: f.body
          }).catch(() => {});
        });
      }

      const initialItems = [
        ...applications.map((app: any) => ({ ...app, syncDate: now, status: 'importing', suggested_offer_id: app.suggested_offer_id })),
        ...normalEmails.map((ne: any) => ({ ...ne, syncDate: now, status: 'pending', suggested_offer_id: ne.suggested_offer_id }))
      ];

      if (initialItems.length > 0) {
        setPendingFiles(prev => {
          const combined = [...initialItems, ...prev];
          const uniqueMap = new Map();
          for (let i = combined.length - 1; i >= 0; i--) {
            const item = combined[i];
            const uniqueKey = `${item.filename}-${item.sender}-${item.subject}`;
            uniqueMap.set(uniqueKey, item);
          }
          return Array.from(uniqueMap.values()).reverse();
        });
      }

      if (applications.length > 0) {
        const MAX_CV_THREADS = 5;
        for (let i = 0; i < applications.length; i += MAX_CV_THREADS) {
          const chunk = applications.slice(i, i + MAX_CV_THREADS);
          
          try {
            const importRes = (provider === 'imap' || provider === 'exchange')
              ? await uploadsApi.importImapPdfs({ files: chunk.map((f:any) => ({ id: f.id, filename: f.filename, subject: f.subject, body: f.body })), team_id: autoSyncTeamId ? String(autoSyncTeamId) : null })
              : await uploadsApi.importGmailPdfs({ files: chunk.map((f:any) => ({ id: f.id, filename: f.filename, subject: f.subject, body: f.body })), team_id: autoSyncTeamId ? String(autoSyncTeamId) : null });
            
            // Dispatch global event for Candidates UI to refresh
            window.dispatchEvent(new CustomEvent('selektia_auto_sync_imported', { detail: { batchId: importRes.data.batch_id } }));

            const notifiedAndProcessedFiles = new Set<string>();
            const errorsShown = new Set<string>();

            const handleBatchUpdate = (currentBatchData: any) => {
              setPendingFiles(prev => prev.map(p => {
                const app = chunk.find((a: any) => a.id === p.id);
                if (app) {
                  const bfItem = currentBatchData?.files?.find((bf: any) => bf.filename === app.filename);
                  const isFileProcessed = currentBatchData?.status === 'completed' || currentBatchData?.status === 'failed' || (bfItem && (bfItem.status === 'completed' || bfItem.status === 'failed'));
                  const isFileFailed = currentBatchData?.status === 'failed' || (bfItem && bfItem.status === 'failed');
                  
                  if (isFileProcessed && !notifiedAndProcessedFiles.has(app.id)) {
                    notifiedAndProcessedFiles.add(app.id);
                    if (isFileFailed) {
                      const reason = bfItem?.error_message || currentBatchData?.error_message || currentBatchData?.reason || t('candidates.emailImport.aiProcessingFailed');
                      if (!errorsShown.has(reason)) {
                        addNotification({ type: 'error', title: t('common.error'), message: reason });
                        errorsShown.add(reason);
                      }
                      setTimeout(() => {
                        setPendingFiles(curr => curr.filter(currP => currP.id !== app.id));
                      }, 2500);

                      uploadsApi.logAutoSyncAction({
                        action: 'import_failed',
                        filename: app.filename,
                        sender: app.sender,
                        subject: app.subject,
                        status: 'failed',
                        details: reason
                      }).catch(() => {});
                    } else {
                      uploadsApi.logAutoSyncAction({
                        action: 'import_success',
                        filename: app.filename,
                        sender: app.sender,
                        subject: app.subject,
                        candidate_id: bfItem?.candidate_id,
                        status: 'success',
                        details: app.body
                      }).catch(() => {});
                    }
                  }

                  if (isFileFailed) return { ...p, status: 'error' };
                  else if (isFileProcessed) return { ...p, status: 'imported', candidate_id: bfItem?.candidate_id, suggested_offer_id: bfItem?.suggested_offer_id || app.suggested_offer_id };
                }
                return p;
              }));
            };

            const batchData = await pollBatchStatus(importRes.data.batch_id, handleBatchUpdate);
            handleBatchUpdate(batchData);
          } catch (e: any) {
            if (e.response?.status === 400 && e.response?.data?.error === 'all_files_duplicate') {
               setPendingFiles(prev => prev.map(p => chunk.some((app: any) => app.id === p.id) ? { ...p, status: 'imported' } : p));
               setTimeout(() => {
                  setPendingFiles(prev => prev.filter(p => !chunk.some((app: any) => app.id === p.id)));
               }, 2500);
            } else {
              setPendingFiles(prev => prev.map(p => chunk.some((app: any) => app.id === p.id) ? { ...p, status: 'error' } : p));
              const errorReason = e.response?.data?.message || e.response?.data?.error || e.message || t('candidates.emailImport.autoImportFailed');
              addNotification({ type: 'error', title: t('common.error'), message: errorReason });
            }
          }
        }
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (e: any) {
      addNotification({ type: 'error', title: t('common.error'), message: e.message || 'Auto-sync failed unexpectedly' });
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (isSyncActive && isConnected) {
      triggerSync(); // Trigger initial sync when activated
      timerRef.current = setInterval(triggerSync, syncInterval * 60 * 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isSyncActive, syncInterval, isConnected]);

  const handleImportPending = async (file: any) => {
    try {
      setIsSyncing(true);
      setPendingFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'importing' } : f));
      
      const connStr = localStorage.getItem('selektia_email_connection');
      if (!connStr) return;
      const { provider } = JSON.parse(connStr);
      
      const importRes = (provider === 'imap' || provider === 'exchange')
        ? await uploadsApi.importImapPdfs({ files: [{ id: file.id, filename: file.filename, subject: file.subject, body: file.body }], team_id: autoSyncTeamId ? String(autoSyncTeamId) : null })
        : await uploadsApi.importGmailPdfs({ files: [{ id: file.id, filename: file.filename, subject: file.subject, body: file.body }], team_id: autoSyncTeamId ? String(autoSyncTeamId) : null });
        
      const batchData = await pollBatchStatus(importRes.data.batch_id);
      const fileData = batchData?.files?.find((bf: any) => bf.filename === file.filename);
      const isError = batchData?.status === 'failed' || (fileData && fileData.status === 'failed');

      if (isError) {
        setPendingFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'error' } : f));
        const reason = fileData?.error_message || batchData?.error_message || batchData?.reason || t('candidates.emailImport.aiProcessingFailedSingle');
        
        uploadsApi.logAutoSyncAction({
          action: 'import_failed',
          filename: file.filename,
          sender: file.sender,
          subject: file.subject,
          status: 'failed',
          details: reason
        }).catch(() => {});

        addNotification({ type: 'error', title: t('common.error'), message: reason });
      } else {
        setPendingFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'imported', syncDate: Date.now(), candidate_id: fileData?.candidate_id, suggested_offer_id: fileData?.suggested_offer_id } : f));
        
        uploadsApi.logAutoSyncAction({
          action: 'import_success',
          filename: file.filename,
          sender: file.sender,
          subject: file.subject,
          candidate_id: fileData?.candidate_id,
          offer_id: fileData?.suggested_offer_id || file.suggested_offer_id,
          status: 'success',
          details: file.body // Ajout du corps de l'email
        }).catch(() => {});

        addNotification({ type: 'success', title: t('common.success', 'Success'), message: t('candidates.emailImport.imported', 'Imported successfully') });
      }

      window.dispatchEvent(new CustomEvent('selektia_auto_sync_imported', { detail: { batchId: importRes.data.batch_id } }));
    } catch(e: any) {
      if (e.response?.status === 400 && e.response?.data?.error === 'all_files_duplicate') {
         setPendingFiles(prev => prev.filter(f => f.id !== file.id)); // Clear from cache instead of marking as imported
         addNotification({ type: 'info', title: t('common.info', 'Information'), message: t('candidates.emailImport.alreadyImported') });
      } else {
         setPendingFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'error' } : f));
         const reason = e.response?.data?.message || e.response?.data?.error || e.message || t('candidates.emailImport.importFailed');
         addNotification({ type: 'error', title: t('common.error'), message: reason });
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeletePending = (fileToRemove: any, permanentlyIgnore: boolean = true) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileToRemove.id));
    if (permanentlyIgnore) {
      const cache = JSON.parse(localStorage.getItem('selektia_auto_sync_deleted_files') || '[]');
      const uniqueKey = `${fileToRemove.filename}-${fileToRemove.sender}-${fileToRemove.subject}`;
      if (!cache.includes(uniqueKey)) {
        cache.push(uniqueKey);
        localStorage.setItem('selektia_auto_sync_deleted_files', JSON.stringify(cache));
      }
    }
  };

  return (
    <AutoSyncContext.Provider value={{
      isSyncActive, setIsSyncActive,
      syncInterval, setSyncInterval,
      autoSyncTeamId, setAutoSyncTeamId,
      lastSyncTime, pendingFiles,
      isSyncing, isConnected,
      triggerSync, handleImportPending, handleDeletePending
    }}>
      {children}
    </AutoSyncContext.Provider>
  );
};

export const useAutoSync = () => {
  const context = useContext(AutoSyncContext);
  if (context === undefined) {
    throw new Error('useAutoSync must be used within an AutoSyncProvider');
  }
  return context;
};
